import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from 'services/api';
import { formatDate } from 'utils/index';
import ScoreRing from 'components/ScoreRing/ScoreRing';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FavoriteIcon from '@mui/icons-material/Favorite';
import HeartBrokenIcon from '@mui/icons-material/HeartBroken';
import BadgeIcon from '@mui/icons-material/Badge';
import ConstructionIcon from '@mui/icons-material/Construction';
import GavelIcon from '@mui/icons-material/Gavel';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import GradeIcon from '@mui/icons-material/Grade';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import SchoolIcon from '@mui/icons-material/School';
import DescriptionIcon from '@mui/icons-material/Description';

import styles from './styles.module.scss';

interface Experience {
  role: string;
  company: string;
  duration: string;
}

interface ApplicationDetail {
  id: number;
  full_name: string;
  email: string;
  phone_number: string;
  telegram: string;
  resume_score: number;
  test_score: number;
  final_score: number;
  created_at: string;
  status: string;
  summary: string | null;
  resume_content: {
    averageWorkInCompany: number;
    totalWorkExperience: number;
    skills: string[];
    education: string[];
    certificates: string[];
    experience: Experience[];
    matches: string[];
    mismatches: string[];
  };
  resume_file_path: string;
  applicant_answers: { questionId: number; answer: string }[];
  ai_questions: { id: number; skill: string; skillType: string; questionText: string; questionReason: string }[];
  ai_feedback: { question: number; answer: string; scoreReason: string; answerScore: number }[];
}

export default function ApplicationDetailPage() {
  const { id, applicationId } = useParams<{ id: string; applicationId: string }>();
  const [application, setApplication] = useState<ApplicationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [openIndexes, setOpenIndexes] = useState<number[]>([]);

  const toggleAccordion = (index: number) => {
    setOpenIndexes(prev => 
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  useEffect(() => {
    if (!applicationId) return;
    api(`applications/${applicationId}?vacancyId=${id}`)
      .then(res => {
        if (res.ok) {
          return res.json();
        }
      })
      .then(data => {
        setApplication(data);

        // Если отклик не просмотрен — отправляем PATCH-запрос
        if (data && !data.is_viewed) {
          api(`applications/${applicationId}/view`, {
            method: 'PATCH'
          }).catch(err => {
            console.error('Ошибка при отметке как просмотренный:', err);
          });
        }
      })
      .catch(err =>  console.error('Ошибка при загрузке отклика:', err))
      .finally(() => setLoading(false));
  }, [applicationId, id]);

  // Устанавливаем заголовок страницы на основе ФИО кандидата
  useEffect(() => {
    if (application && application.full_name) {
      document.title = application.full_name;
    }
  }, [application]);
  
  const generateStatusText = (status: string) => {
    if (status === 'ANALYSED') {
      return 'Неразобранные';
    }
    if (status === 'HOLD') {
      return 'Интересные';
    }

    if (status === 'REJECTED') {
      return 'Отклонённые';
    }

    return '';
  }

  const generateTestConclusion = (score: number): string => {
    if (score >= 80) {
      return 'хорошо';
    }
    if (score > 50) {
      return 'удовлетворительно';
    }

    if (score <= 50) {
      return 'плохо';
    }

    return '';
  }

  const generateTestConclusionScore = (score: number) => {
    if (score >= 80) {
      return '#6bde83';
    }
    if (score > 50) {
      return '#dbde6b';
    }

    if (score <= 50) {
      return '#de6b6b';
    }

    return 'gray';
  }

  const formatWorkExperience = (months: number): string => {
    if (!months || months < 0) return '—';

    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;

    const yearPart = years > 0 ? `${years} ${years === 1 ? 'год' : (years < 5 ? 'года' : 'лет')}` : '';
    const monthPart = remainingMonths > 0 ? `${remainingMonths} ${remainingMonths === 1 ? 'месяц' : (remainingMonths < 5 ? 'месяца' : 'месяцев')}` : '';

    if (yearPart && monthPart) return `${yearPart} и ${monthPart}`;
    return yearPart || monthPart;
  };

  if (loading) return null;
  if (!application) return (
    <div className={styles.status}>
      <p>Отклик не найден</p>
      <SentimentVeryDissatisfiedIcon />
    </div>
  );

  const goodAnswersCount = (application && application.ai_feedback) ? application.ai_feedback.filter(f => f.answerScore >= 80).length : 0;

  return (
    <div className={styles.container}>
      <div className={styles.breadcrumbs}>
        <Link to={`/vacancies/${id}`} className={styles.backLink}>← К вакансии</Link>
      </div>
      <p className={styles.applicationDate}>{formatDate(application.created_at)}</p>
      <div className={styles.titleWrapper}>
        <div>
          <h2>{application.full_name} <span className={styles.applicationStatus}>{generateStatusText(application.status)}</span></h2>
          <div className={styles.contactsWrapper}>
            <p className={styles.contact}><strong>Почта:</strong> {application.email}&nbsp;&nbsp;|&nbsp;&nbsp;</p>
            <p className={styles.contact}><strong>Телефон:</strong> {application.phone_number || '-'}&nbsp;&nbsp;|&nbsp;&nbsp;</p>
            <p className={styles.contact}><strong>Telegram:</strong> {application.telegram  || '-'}</p>
          </div>
        </div>
        <div className={styles.scoreWrapper}>
          <div>
            <p>Резюме</p>
            <ScoreRing score={application.resume_score} />
          </div>
          <div>
            <p>Тест</p>
            <ScoreRing score={application.test_score} />
          </div>
          <div>
            <p>Итог</p>
            <ScoreRing score={application.final_score} />
          </div>
        </div>
      </div>

      {application.summary && (
        <div>
          <h3 className={styles.analysisResumeTitle}>Заключение</h3>
          <div className={styles.wrapper}>
            <p>{application.summary}</p>
          </div>
        </div>
      )}

      <h3 className={styles.analysisResumeTitle}>
        Анализ резюме
        <a href={application.resume_file_path} target="_blank" rel="noopener noreferrer" className={styles.pdfLink}>
          <FileDownloadIcon /> Скачать резюме
        </a>
      </h3>
      <div className={styles.wrapper}>
        <div className={styles.matchMismatchWrapper}>
          <div className={styles.section}>
            <h3 className={styles.strengths}>Сильные стороны&nbsp;<FavoriteIcon /></h3>
            <ul>
                            {
                application.resume_content.matches && application.resume_content.matches.length === 0 ? '—' :
                 application.resume_content.matches?.map(m => <li key={m}>{m}</li>)
              }
            </ul>
          </div>

          <div className={styles.section}>
            <h3 className={styles.weaknesses}>Слабые стороны&nbsp;<HeartBrokenIcon /></h3>
            <ul>
              {
                application.resume_content.mismatches && application.resume_content.mismatches.length === 0 ? '—' :
                 application.resume_content.mismatches?.map(m => <li key={m}>{m}</li>)
              }
            </ul>
          </div>
        </div>
        <div className={styles.experience}>
          <h3>
            Опыт работы <BadgeIcon />
          </h3>
          <div className={styles.experienceExpanded}>
            <p>
              <strong>Общий:</strong> {formatWorkExperience(application.resume_content?.totalWorkExperience)}&nbsp;&nbsp;|&nbsp;&nbsp;
            </p>
            <p>
              <strong> Средний стаж в компании:</strong> {formatWorkExperience(application.resume_content.averageWorkInCompany)}
            </p>
          </div>
          <ul>
            {application.resume_content?.experience && application.resume_content?.experience.length === 0 ? '—' : 
            application.resume_content?.experience?.map((exp, i) => (
              <li key={i}>
                <strong>{exp.role}</strong> — {exp.company} ({typeof exp.duration === 'string' ? exp.duration : formatWorkExperience(exp.duration)})
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className={styles.resumeSkillsTitle}>Образование <SchoolIcon /></h3>
          <p>{application.resume_content?.education?.join(', ') || '—'}</p>
        </div>
        <div>
          <h3 className={styles.resumeSkillsTitle}>Сертификаты <DescriptionIcon /></h3>
          <p>{application.resume_content?.certificates?.join(', ') || '—'}</p>
        </div>
        <div className={styles.section}>
          <h3 className={styles.resumeSkillsTitle}>Навыки из резюме <ConstructionIcon /></h3>
          <p>{application.resume_content?.skills?.join(', ') || '—'}</p>
        </div>
      </div>
      <h3>Анализ теста</h3>
      <div className={styles.wrapper}>
        <div className={styles.conclusionWrapper}>
          <div>
            <p className={styles.conclusionQuestions}>{(application.ai_feedback && application.ai_feedback.length) || (application.ai_questions && application.ai_questions.length) || '—'}</p>
            <p className={styles.conclusionTitle}>Всего вопросов</p>
          </div>
          <div>
            <p 
              className={styles.conclusionScore} 
              style={{ color: generateTestConclusionScore(application.test_score) }}
            >
              {application.test_score} <span>из</span> <span>100</span>
            </p>
            <p className={styles.conclusionTitle}>Набрано балов</p>
          </div>
          <div>
            <p className={styles.goodAnswers}>{goodAnswersCount}</p>
            <p className={styles.conclusionTitle}>Хорошие ответы</p>
          </div>
          <div className={styles.conclusionResult}>
            <p style={{ color: generateTestConclusionScore(application.test_score) }}><GavelIcon />{generateTestConclusion(application.test_score)}</p>
            <p className={styles.conclusionTitle}>Вердикт</p>
          </div>
        </div>

            {(application.ai_feedback?.length > 0
            ? application.ai_feedback.map((feedback, index) => {
                const isOpen = openIndexes.includes(index);
                return (
                  <div key={index} className={styles.accordionItem}>
                    <div className={styles.scoreReason} onClick={() => toggleAccordion(index)}>
                      <div className={styles.scoreTextWrapper}>
                        <div className={styles.scoreQuestionNumber}>
                          <div>Вопрос #{index + 1}</div>
                          <div className={styles.scoreArrowWrapper}>
                            <div>
                              <div
                                className={styles.scoreNumber}
                                style={{ background: generateTestConclusionScore(feedback.answerScore) }}
                              >
                                {feedback.answerScore}
                                <GradeIcon />
                              </div>
                            </div>
                            <div className={`${styles.arrowDownIcon} ${isOpen ? styles.active : ''}`}>
                              <KeyboardArrowDownIcon />
                            </div>
                          </div>
                        </div>
                        <div>{feedback.scoreReason}</div>
                      </div>
                    </div>
                    <div className={`${styles.feedbackDetail} ${isOpen ? styles.open : ''}`}>
                      <div className={styles.feedbackOpened}>
                        <div className={styles.question}>
                          <div className={styles.whoIs}><span>Wizex AI</span></div>
                          {feedback.question}
                        </div>
                        <div className={styles.answer}>
                          <div className={styles.whoIs}><span>{application.full_name}</span></div>
                          {feedback.answer || '–'}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }) :
              application.ai_questions.map((question, index) => {
                const isOpen = openIndexes.includes(index);
                return (
                  <div key={index} className={styles.accordionItem}>
                    <div className={styles.scoreReason} onClick={() => toggleAccordion(index)}>
                      <div className={styles.scoreTextWrapper}>
                        <div className={styles.scoreQuestionNumber}>
                          <div>Вопрос #{index + 1}</div>
                        <div className={styles.scoreArrowWrapper}>
                          <div>
                            <div
                              className={styles.scoreNumber}
                              style={{ background: generateTestConclusionScore(0) }}
                            >
                              0
                              <GradeIcon />
                            </div>
                          </div>
                          <div className={`${styles.arrowDownIcon} ${isOpen ? styles.active : ''}`}>
                            <KeyboardArrowDownIcon />
                          </div>
                        </div>
                      </div>
                      <div>{question.questionReason || 'Причина не указана'}</div>
                    </div>
                  </div>
                  <div className={`${styles.feedbackDetail} ${isOpen ? styles.open : ''}`}>
                    <div className={styles.feedbackOpened}>
                      <div className={styles.question}>
                        <div className={styles.whoIs}><span>Wizex AI</span></div>
                        {question.questionText}
                      </div>
                      <div className={styles.answer}>
                        <div className={styles.whoIs}><span>{application.full_name}</span></div>
                        {'–'}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )
        }
      </div>
    </div>
  );
}
